import { Link, useParams } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!slug || !article) {
    return (
      <>
        <SEO
          title="文章未找到"
          description="抱歉，您要查看的文章不存在或已被移除。"
          canonicalUrl={`https://mengnan-danshengji.com/article/${slug}`}
        />
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="mt-6 text-2xl font-bold font-heading text-foreground-950">文章未找到</h1>
            <p className="mt-2 text-foreground-500">抱歉，您要查看的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-block mt-6 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-md transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    publisher: {
      '@type': 'Organization',
      name: '猛男诞生记',
      url: 'https://mengnan-danshengji.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://mengnan-danshengji.com/article/${article.slug}`,
    },
    keywords: article.tags.join(','),
    articleSection: article.categoryName,
    wordCount: article.content.length,
    timeRequired: `PT${article.readTime}M`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mengnan-danshengji.com/' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://mengnan-danshengji.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://mengnan-danshengji.com/article/${article.slug}`}
        ogImage={article.coverImage}
        ogType="article"
        schema={{ ...articleSchema, ...breadcrumbSchema }}
        articleData={{
          publishedTime: article.date,
          modifiedTime: article.date,
          author: article.author,
          tags: article.tags,
        }}
      />

      <Navbar />

      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="mt-4">
            <span className="inline-block px-3 py-1 text-sm font-medium bg-accent-100 text-accent-700 rounded-md">
              {article.categoryName}
            </span>
          </div>

          <h1 className="mt-4 text-2xl md:text-4xl font-bold font-heading text-foreground-950 leading-tight">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-foreground-500">
            <span className="flex items-center gap-2">
              <img src={article.authorAvatar} alt={article.author} className="w-7 h-7 rounded-full object-cover" />
              {article.author}
            </span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>{article.date}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>{article.readTime} 分钟阅读</span>
          </div>

          <div className="mt-8 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          </div>

          <div
            className="mt-8 md:mt-10 prose prose-base md:prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-foreground-900
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
              prose-p:text-foreground-700 prose-p:leading-relaxed
              prose-strong:text-foreground-900
              prose-ul:text-foreground-700 prose-li:my-1.5
              prose-ol:text-foreground-700
              [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 flex flex-wrap items-center gap-2">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 text-xs font-medium bg-background-100 text-foreground-600 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden hover:bg-background-200/50 transition-colors cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-400">{ra.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}